import { useEffect, useState } from "react";
import { FileText, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { nb } from "@/lib/format";
import { EMPTY } from "./constants";
import { SignatoryFields, validateSignatories } from "./SignatoryFields";

export function NewSettlementDialog({ companyId, rows, onGenerated, open: openProp, onOpenChange, showTrigger = true }) {
  const [openState, setOpenState] = useState(false);
  const open = openProp !== undefined ? openProp : openState;
  const setOpen = (o) => (onOpenChange ? onOpenChange(o) : setOpenState(o));
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (open) setForm((f) => (f.date ? f : { ...EMPTY, date: new Date().toISOString().slice(0, 10) }));
  }, [open]);

  const sellerRow = rows.find((r) => r.id === form.seller_shareholder_id);
  const sellerClasses = sellerRow ? Object.keys(sellerRow.holdings).filter((k) => sellerRow.holdings[k] > 0) : [];
  const maxShares = sellerRow && form.share_class ? sellerRow.holdings[form.share_class] || 0 : 0;
  const sellerIsCompany = sellerRow?.holder_type === "selskap";
  const buyerRow = rows.find((r) => r.id === form.buyer_shareholder_id);
  const buyerIsCompany =
    form.buyerKind === "new_company" || (form.buyerKind === "existing" && buyerRow?.holder_type === "selskap");

  const generate = async () => {
    if (!form.date || !form.seller_shareholder_id || !form.share_class || !form.shares) {
      toast.error("Fyll inn dato, selger, aksjeklasse og antall");
      return;
    }
    const shares = parseInt(form.shares, 10);
    if (shares > maxShares) {
      toast.error(`Selger eier bare ${nb(maxShares)} ${form.share_class}`);
      return;
    }
    if (form.buyerKind === "existing" && !form.buyer_shareholder_id) return toast.error("Velg kjøper");
    if (form.buyerKind !== "existing" && !form.buyer_name) return toast.error("Skriv inn navn på kjøper");
    if (form.buyerKind === "new_person" && !form.buyer_email?.trim()) return toast.error("Skriv inn e-post på kjøper (fødselsnummer kommer via BankID)");
    if (sellerIsCompany) {
      const err = validateSignatories(form.seller_signatories, sellerRow?.name);
      if (err) return toast.error(err);
    }
    if (buyerIsCompany) {
      const err = validateSignatories(form.buyer_signatories, form.buyerKind === "existing" ? buyerRow?.name : form.buyer_name);
      if (err) return toast.error(err);
    }
    const buyer =
      form.buyerKind === "existing"
        ? { kind: "existing", shareholder_id: form.buyer_shareholder_id }
        : form.buyerKind === "new_person"
        ? { kind: "new_person", name: form.buyer_name, id_number: form.buyer_id_number, email: form.buyer_email }
        : { kind: "new_company", name: form.buyer_name, id_number: form.buyer_id_number };
    if (buyerIsCompany) {
      buyer.signatories = form.buyer_signatories;
    }
    const seller_signatories = sellerIsCompany ? form.seller_signatories : undefined;
    setSaving(true);
    try {
      await api.post(`/companies/${companyId}/settlements`, {
        date: form.date, share_class: form.share_class, shares,
        price_per_share: parseFloat(form.price_per_share || 0), note: form.note,
        seller_shareholder_id: form.seller_shareholder_id, seller_signatories, buyer,
      });
      toast.success("Sluttseddel generert – klar for signering");
      setOpen(false);
      setForm(EMPTY);
      onGenerated();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setForm({ ...EMPTY, date: new Date().toISOString().slice(0, 10) }); }}>
      {showTrigger ? (
        <DialogTrigger asChild>
          <Button className="gap-2 active:scale-[0.98]" data-testid="new-settlement-btn"><FileText className="h-4 w-4" /> Generer sluttseddel</Button>
        </DialogTrigger>
      ) : null}
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Generer sluttseddel</DialogTitle>
          <DialogDescription>Selger kan kun selge aksjer hen eier. Begge parter signerer digitalt, og overføringen gjennomføres automatisk.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Selger (aksjonær)</Label>
            <Select value={form.seller_shareholder_id} onValueChange={(v) => setForm({ ...form, seller_shareholder_id: v, share_class: "" })}>
              <SelectTrigger className="mt-1.5" data-testid="settlement-seller-select"><SelectValue placeholder="Velg selger" /></SelectTrigger>
              <SelectContent>
                {rows.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} ({nb(r.shares)} aksjer)</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {sellerIsCompany ? (
            <SignatoryFields companyName={sellerRow?.name} testIdPrefix="settlement-seller-signatory"
              orgNumber={sellerRow?.id_number}
              value={form.seller_signatories} onChange={(v) => setForm({ ...form, seller_signatories: v })} />
          ) : null}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Aksjeklasse</Label>
              <Select value={form.share_class} onValueChange={(v) => setForm({ ...form, share_class: v })} disabled={!sellerRow}>
                <SelectTrigger className="mt-1.5" data-testid="settlement-class-select"><SelectValue placeholder="Velg klasse" /></SelectTrigger>
                <SelectContent>
                  {sellerClasses.map((k) => <SelectItem key={k} value={k}>{k} ({nb(sellerRow.holdings[k])})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Antall aksjer {form.share_class ? <span className="text-muted-foreground">(maks {nb(maxShares)})</span> : null}</Label>
              <Input type="number" data-testid="settlement-shares-input" className="mt-1.5" value={form.shares} max={maxShares || undefined} onChange={(e) => setForm({ ...form, shares: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Dato</Label><Input type="date" data-testid="settlement-date-input" className="mt-1.5" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            <div><Label>Kurs per aksje (kr)</Label><Input type="number" className="mt-1.5" value={form.price_per_share} onChange={(e) => setForm({ ...form, price_per_share: e.target.value })} /></div>
          </div>

          <div>
            <Label>Kjøper</Label>
            <div className="mt-1.5 flex gap-1.5 rounded-lg border border-border p-1">
              {[["existing", "Eksisterende"], ["new_person", "Ny person"], ["new_company", "Ny bedrift"]].map(([v, l]) => (
                <button key={v} type="button" data-testid={`buyer-kind-${v}`} onClick={() => setForm({ ...form, buyerKind: v })}
                  className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${form.buyerKind === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          {form.buyerKind === "existing" ? (
            <Select value={form.buyer_shareholder_id} onValueChange={(v) => setForm({ ...form, buyer_shareholder_id: v })}>
              <SelectTrigger data-testid="settlement-buyer-select"><SelectValue placeholder="Velg kjøper" /></SelectTrigger>
              <SelectContent>
                {rows.filter((r) => r.id !== form.seller_shareholder_id).map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="flex items-center gap-1.5">Navn
                  {form.buyerKind === "new_company" && form.buyer_name_verified ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600" data-testid="settlement-buyer-name-verified">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Bekreftet mot Proff
                    </span>
                  ) : null}
                </Label>
                <Input data-testid="settlement-buyer-name" className="mt-1.5" value={form.buyer_name} onChange={(e) => setForm({ ...form, buyer_name: e.target.value, buyer_name_verified: false })} placeholder={form.buyerKind === "new_company" ? "Selskapsnavn AS" : "Fornavn Etternavn"} />
              </div>
              <div><Label>{form.buyerKind === "new_company" ? "Org.nr" : "Fødselsnummer (valgfritt)"}</Label><Input data-testid="settlement-buyer-idnr" className="mt-1.5" value={form.buyer_id_number} onChange={(e) => setForm({ ...form, buyer_id_number: e.target.value, buyer_name_verified: false })} placeholder={form.buyerKind === "new_person" ? "Kommer via BankID" : ""} /></div>
              {form.buyerKind === "new_person" ? <div><Label>E-post</Label><Input data-testid="settlement-buyer-email" type="email" className="mt-1.5" value={form.buyer_email} onChange={(e) => setForm({ ...form, buyer_email: e.target.value })} placeholder="navn@epost.no" /></div> : null}
            </div>
          )}

          {buyerIsCompany ? (
            <SignatoryFields companyName={form.buyerKind === "existing" ? buyerRow?.name : form.buyer_name} testIdPrefix="settlement-buyer-signatory"
              orgNumber={form.buyerKind === "existing" ? buyerRow?.id_number : form.buyer_id_number}
              onProffName={form.buyerKind === "existing" ? undefined : (n) => setForm((f) => ({ ...f, buyer_name: n }))}
              onVerified={form.buyerKind === "existing" ? undefined : () => setForm((f) => ({ ...f, buyer_name_verified: true }))}
              value={form.buyer_signatories} onChange={(v) => setForm({ ...form, buyer_signatories: v })} />
          ) : null}

          <div><Label>Merknad (valgfritt)</Label><Textarea className="mt-1.5" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Avbryt</Button>
          <Button onClick={generate} disabled={saving} data-testid="generate-settlement-btn" className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Generer sluttseddel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
